import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from '@auth/AuthContext';

import ErrorBoundary from '@components/ui/ErrorBoundary';
import { ToastProvider } from '@components/ui/Toast';
import { ConfirmProvider } from '@components/ui/ConfirmProvider';
import { UnsavedGuardProvider } from '@components/ui/UnsavedGuard';

import App from './App.jsx';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './styles/global.css';

/* ════════════════════════════════════════════════════════════════════
   QueryClient configuration
   These defaults govern how the app handles caching, background refetch,
   retries, and stale data. Override per-query when needed.
   ════════════════════════════════════════════════════════════════════ */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data stays "fresh" for 60 seconds — no automatic refetch in this window
      staleTime: 60_000,
      // Garbage-collect after 5 minutes of no observers
      gcTime: 5 * 60_000,
      // Retry failed requests 2x with exponential backoff
      retry: 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30_000),
      // Don't refetch every active query when the user switches back to the
      // tab — that stampedes the API (esp. behind a single-threaded dev server)
      // for little benefit. Data still refetches on mount once it goes stale,
      // and long-lived screens use their own refetchInterval.
      refetchOnWindowFocus: false,
      // Don't auto-refetch on reconnect — most queries are still fresh
      refetchOnReconnect: 'always',
    },
    mutations: {
      retry: 1,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary scope="app">
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <ToastProvider>
              <ConfirmProvider>
                <UnsavedGuardProvider>
                  <App />
                </UnsavedGuardProvider>
              </ConfirmProvider>
            </ToastProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
